import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import type { LanguageCode } from '../i18n';
import { clearMapStyleCache } from '../lib/map-style';

export function useLanguage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  // On login: load language from profile
  useEffect(() => {
    if (user) {
      supabase
        .from('profiles')
        .select('preferred_language')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.preferred_language && data.preferred_language !== i18n.language) {
            i18n.changeLanguage(data.preferred_language);
          }
        });
    }
  }, [user]);

  // Change language + persist
  const changeLanguage = useCallback(async (lang: LanguageCode) => {
    await i18n.changeLanguage(lang);
    clearMapStyleCache(); // Force maps to reload with new language labels
    localStorage.setItem('tg_language', lang);

    // Sync to profile if logged in
    if (user) {
      supabase
        .from('profiles')
        .update({ preferred_language: lang })
        .eq('id', user.id)
        .then(() => {});
    }
  }, [i18n, user]);

  return {
    currentLanguage: i18n.language as LanguageCode,
    changeLanguage,
    t: i18n.t,
  };
}
